import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Smartphone, Monitor, Tablet, ChevronDown, PenSquare, Eye } from "lucide-react";
import { useDeviceSettings } from "@/hooks/useDeviceSettings";
import { supabase } from "../lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { DeviceCardContent } from "./DeviceCardContent";
import { motion } from "framer-motion";

interface Screen {
  id: string;
  name: string;
  settings?: {
    description?: string;
  };
}

interface DeviceCardProps {
  device: {
    id: string;
    name: string;
    code: string;
    status: 'active' | 'inactive';
    screen_id?: string;
    activated_at: string;
    user_id: string;
  };
  screens: Screen[];
  onDelete: (deviceId: string) => void;
  onUpdateScreen: (deviceId: string, screenId: string) => void;
  onUpdateSettings?: (settings: any) => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

export default function DeviceCard({
  device,
  screens,
  onDelete,
  onUpdateScreen,
  onUpdateSettings,
  isExpanded,
  onToggleExpand
}: DeviceCardProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false); // New state for preview modal
  const [newDeviceName, setNewDeviceName] = useState(device.name);
  const { settings, loading, updateSettings } = useDeviceSettings(device.code);
  const { toast } = useToast();

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleExpand();
  };

  const handleDeleteDevice = async () => {
    setIsDeleting(true);
    try {
      await onDelete(device.id);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUpdateName = async () => {
    if (!newDeviceName.trim()) {
      toast({
        title: "Nome inválido",
        description: "O nome do dispositivo não pode ficar vazio",
        variant: "destructive"
      });
      return;
    }

    setIsUpdating(true);
    try {
      console.log('🔄 Atualizando nome do dispositivo:', { deviceId: device.id, newName: newDeviceName });

      // Atualizar no banco de dados - apenas os campos necessários
      const { data, error } = await supabase
        .from('devices')
        .update({ name: newDeviceName.trim() })
        .eq('id', device.id)
        .select()
        .single();

      if (error) throw error;

      // Atualizar dados de ativação do player
      const activationKey = `device_activation_${device.code}`;
      const currentActivation = localStorage.getItem(activationKey);
      if (currentActivation) {
        const activation = JSON.parse(currentActivation);
        const updatedActivation = {
          ...activation,
          deviceName: newDeviceName.trim(),
          timestamp: Date.now()
        };
        localStorage.setItem(activationKey, JSON.stringify(updatedActivation));

        // Disparar evento para atualização do player
        window.dispatchEvent(new CustomEvent('deviceUpdateSync', {
          detail: {
            deviceCode: device.code,
            name: newDeviceName.trim(),
            ...updatedActivation
          }
        }));
      }

      toast({
        title: "Nome atualizado",
        description: "O nome do dispositivo foi alterado com sucesso"
      });

      setIsEditingName(false);

    } catch (error) {
      console.error('Erro ao atualizar nome:', error);
      toast({
        title: "Erro ao atualizar",
        description: "Não foi possível alterar o nome do dispositivo",
        variant: "destructive"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleScreenChange = async (screenId: string) => {
    setIsUpdating(true);
    try {
      console.log('🔄 Iniciando atualização de tela:', { deviceId: device.id, screenId });

      // Preparar dados para atualização
      const { error } = await supabase
        .from('devices')
        .update({
          screen_id: screenId,
          status: 'active',
          activated_at: new Date().toISOString()
        })
        .match({ id: device.id });

      if (error) {
        console.error('Erro SQL:', error);
        throw error;
      }

      // Atualizar estado local via callback
      onUpdateScreen(device.id, screenId);

      // Atualizar dados de ativação para o player
      const activationKey = `device_activation_${device.code}`;
      const selectedScreen = screens.find(s => s.id === screenId);

      const updatedActivation = {
        screenId: screenId,
        screenTitle: selectedScreen?.name || '',
        account: device.user_id,
        timestamp: Date.now(),
        deviceCode: device.code,
        status: 'active'
      };

      localStorage.setItem(activationKey, JSON.stringify(updatedActivation));
      console.log('📱 Dados de ativação atualizados:', updatedActivation);

      // Disparar eventos de sincronização para o player
      window.dispatchEvent(new CustomEvent('deviceScreenSync', {
        detail: { deviceCode: device.code, screenId, screenTitle: selectedScreen?.name }
      }));

      window.dispatchEvent(new CustomEvent('deviceUpdateSync', {
        detail: updatedActivation
      }));

      toast({
        title: "Tela atualizada",
        description: `A tela ${selectedScreen?.name || 'selecionada'} foi associada com sucesso`
      });

    } catch (error) {
      console.error('Erro ao atualizar tela:', error);
      toast({
        title: "Erro ao atualizar",
        description: "Não foi possível atualizar a tela. Por favor, tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleClock = async (enabled: boolean) => {
    await updateSettings({ showClock: enabled });
  };

  const handleToggleYoutube = async (enabled: boolean) => {
    await updateSettings({ showYoutube: enabled });

    // Disparar evento de sincronização para atualizar o player
    const activationKey = `device_activation_${device.code}`;
    const activationStr = localStorage.getItem(activationKey);
    if (activationStr) {
      try {
        const activation = JSON.parse(activationStr);
        const updatedActivation = {
          ...activation,
          settings: {
            ...activation.settings,
            showYoutube: enabled
          },
          timestamp: Date.now()
        };
        localStorage.setItem(activationKey, JSON.stringify(updatedActivation));

        // Disparar evento para atualização do player
        window.dispatchEvent(new CustomEvent('deviceSettingsSync', {
          detail: {
            deviceCode: device.code,
            settings: {
              showYoutube: enabled
            }
          }
        }));
      } catch (error) {
        console.error('Erro ao sincronizar configurações do YouTube:', error);
      }
    }
  };

  const handleSaveDeviceName = async () => {
    if (!newDeviceName.trim()) {
      toast({
        title: "Nome inválido",
        description: "O nome do dispositivo não pode ficar vazio",
        variant: "destructive"
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('devices')
        .update({
          name: newDeviceName.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', device.id);

      if (error) throw error;

      toast({
        title: "Nome atualizado",
        description: "O nome do dispositivo foi atualizado com sucesso"
      });

      setIsEditingName(false);

    } catch (error) {
      console.error('Erro ao atualizar nome:', error);
      toast({
        title: "Erro ao atualizar",
        description: "Não foi possível atualizar o nome do dispositivo",
        variant: "destructive"
      });
    }
  };

  return (
    <Card className={cn(
      "w-full transition-all duration-200 ease-in-out shadow-sm hover:shadow-md",
      isExpanded ? "bg-background" : "bg-[#f3f9f3]"
    )}>
      <CardContent className="p-4">
        <motion.div
          className="flex flex-col gap-4"
          layout
          transition={{ duration: 0.2 }}
        >
          {/* Header - sempre visível */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1">
              <Monitor className="h-5 w-5 text-muted-foreground" />
              <Tablet className="h-5 w-5 text-muted-foreground -ml-2" />
              {isEditingName ? (
                <div className="flex items-center gap-2 flex-1">
                  <Input
                    value={newDeviceName}
                    onChange={(e) => setNewDeviceName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleUpdateName();
                      } else if (e.key === 'Escape') {
                        setIsEditingName(false);
                        setNewDeviceName(device.name);
                      }
                    }}
                    className="h-8"
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                    placeholder="Digite o novo nome..."
                    disabled={isUpdating}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUpdateName();
                    }}
                    disabled={isUpdating || !newDeviceName.trim()}
                  >
                    Salvar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditingName(false);
                      setNewDeviceName(device.name);
                    }}
                    disabled={isUpdating}
                  >
                    Cancelar
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col">
                  <span className="font-semibold text-lg text-foreground">{device.name}</span>
                  <span className="text-xs text-muted-foreground font-mono mt-1">Cód. ativação: <b>{device.code}</b></span>
                </div>
              )}
              <Badge variant={device.status === 'active' ? "outline" : "secondary"}
                className={device.status === 'active' ? "border-green-500 text-green-500" : ""}>
                {device.status === 'active' ? 'Ativo' : 'Inativo'}
              </Badge>
              {/* Botão de preview */}
              <Button
                variant="outline"
                size="icon"
                title="Visualizar Player"
                className="ml-2"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsPreviewOpen(true);
                }}
              >
                <Eye className="h-5 w-5" />
              </Button>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "transition-transform",
                isExpanded && "rotate-180"
              )}
              onClick={handleToggle}
            >
              <ChevronDown className="h-5 w-5" />
            </Button>
          </div>

          {/* Conteúdo expansível */}
          <motion.div
            initial={false}
            animate={{
              height: isExpanded ? "auto" : 0,
              opacity: isExpanded ? 1 : 0
            }}
            transition={{ duration: 0.2 }}
            style={{ overflow: 'hidden' }}
          >
            <DeviceCardContent
              device={device}
              screens={screens}
              isUpdating={isUpdating}
              loading={loading}
              isDeleting={isDeleting}
              onScreenChange={handleScreenChange}
              onToggleClock={handleToggleClock}
              isExpanded={isExpanded}
              onToggleNewsTicker={(enabled) => handleToggleNewsTicker(enabled)}
              onToggleYoutube={handleToggleYoutube}
              onDelete={() => handleDeleteDevice()}
              settings={settings || {}}
              onUpdateSettings={async (partial) => {
                await updateSettings(partial);
              }}
              onUpdateWeatherConfig={async (config) => {
                try {
                  // Validar duração máxima de 20 segundos
                  const safeConfig = {
                    ...config,
                    duration: Math.min(20, Math.max(1, config.duration))
                  };

                  console.log('Update weather:', safeConfig);

                  const { error } = await supabase
                    .from('devices')
                    .update({ weather_config: safeConfig })
                    .eq('id', device.id);

                  if (error) throw error;

                  // Atualizar ativação local e disparar evento
                  const activationKey = `device_activation_${device.code}`;
                  const currentStr = localStorage.getItem(activationKey);

                  // Criar ou atualizar a entrada no localStorage
                  const current = currentStr ? JSON.parse(currentStr) : { deviceCode: device.code };
                  const updated = { ...current, weather_config: safeConfig, timestamp: Date.now() };
                  localStorage.setItem(activationKey, JSON.stringify(updated));

                  window.dispatchEvent(new CustomEvent('deviceUpdateSync', {
                    detail: updated
                  }));

                  toast({ title: 'Configuração atualizada' });

                } catch (err) {
                  console.error(err);
                  toast({ title: 'Erro ao salvar configuração de tempo', variant: 'destructive' });
                }
              }}
            />
          </motion.div>
        </motion.div>

        {/* Modal de Preview do Player */}
        <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
          <DialogContent
            className="max-w-5xl w-full p-0 gap-0 bg-gray-900 border-gray-800 overflow-hidden text-white flex flex-col"
            style={{ height: '80vh', top: '8%', transform: 'translateX(-50%)' }}
          >
            <DialogHeader className="px-4 py-3 border-b border-gray-800 bg-gray-900 flex-shrink-0">
              <DialogTitle className="flex items-center gap-2 text-white">
                <Monitor className="w-5 h-5 text-green-500" />
                Preview em Tempo Real - {device.name}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Visualização em tempo real do player do dispositivo {device.name}
              </DialogDescription>
            </DialogHeader>
            <div className="w-full bg-black relative flex-1 min-h-0">
              <iframe
                src={`/player?code=${encodeURIComponent(device.code)}&user_id=${encodeURIComponent(device.user_id)}`}
                className="w-full h-full border-0 absolute inset-0"
                allow="autoplay; fullscreen; picture-in-picture"
                title={`Preview do Player ${device.name}`}
              />
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
